import React, { useEffect, useState } from "react";
import { Col, Form, Row, Spinner } from "react-bootstrap";
import CustomButton from "../common/CustomButton";
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";
import ArrowCircleLeftRoundedIcon from "@mui/icons-material/ArrowCircleLeftRounded";
import CustomTextFeild from "../common/CustomTextFeild";
import { useNavigate, useParams } from "react-router-dom";
import { apiRequest } from "../utils/config/apiRequest";
import { _apiUrls } from "../utils/endpoints/apiUrls";
import { validateUserForm } from "../common/Validation";
import { getTheme } from "../redux/selectors/appSelectors";
import { useSelector } from "react-redux";
import CustomSelect from "../common/CustomSelect";
import { compressImageIfNeeded } from "../utils/endpoints/filehelper";
import MapLocationPicker from "./MapLocationPicker";

export default function AddAdmin() {
  const theme = useSelector(getTheme);
  const isDarkMode = theme === "dark";

  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [errors, setErrors] = useState({});
  const [checkStatus, setCheckStatus] = useState("");
  const [state, setState] = useState({
    fullName: "",
    email: "",
    mobile: "",
    password: "",
    country: null,
    profilePic: null,
    latitude: null,
    longitude: null,
  });

  const [roles, setRoles] = useState({
    adminId: "",
    isUserEdit: false,
    isNotificationEdit: false,
    isUserDelete: false,
    isNotificationDelete: false,
    isCategoryEdit: false,
    isCategoryDelete: false,
    isReportView: false,
    isReportExport: false,
    isPackagesView: false,
    isContactsView: false,
  });

  const [load, setLoad] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showMapPicker, setShowMapPicker] = useState(false);

  const handleChange = (value) => {
    setCheckStatus(value.toLowerCase());
  };

  const [country, setCountry] = useState({
    rows: [],
  });
  const getCountry = async () => {
    const { data, status } = await apiRequest({
      endUrl: _apiUrls.fetchCountry,
      method: "GET",
    });
    if (status) {
      const formattedCountry = data?.rows?.map((item) => ({
        label: item?.name,
        value: item?._id,
      }));
      setCountry({ rows: formattedCountry });
    }
  };

  const handleSelectChange = (selected) => {
    setState((prev) => ({
      ...prev,
      country: selected?.value || "",
    }));
  };
  const handleLocationSelect = (location) => {
    setState((prev) => ({
      ...prev,
      latitude: location.lat,
      longitude: location.lng,
    }));
  };

  // Add Edit -------------------------------------------------

  const fetchUserDetails = async () => {
    setLoadingData(true);
    const { data, status } = await apiRequest({
      endUrl: _apiUrls.getAllAdmins,
      method: "GET",
      query: { adminId: id },
    });
    setLoadingData(false);
    const user = data?.rows;
    if (status && user) {
      setState({
        fullName: user?.fullName,
        email: user?.email,
        mobile: user?.mobile,
        password: "",
        country: user?.country?._id,
        profilePic: user?.profilePic,
        latitude: user?.latitude,
        longitude: user?.longitude,
      });
      setCheckStatus(user?.status?.toLowerCase());
      if (user?.adminRoles) {
        const cleanedRoles = {
          isUserEdit: user.adminRoles.isUserEdit || false,
          isNotificationEdit: user.adminRoles.isNotificationEdit || false,
          isUserDelete: user.adminRoles.isUserDelete || false,
          isNotificationDelete: user.adminRoles.isNotificationDelete || false,
          isCategoryEdit: user.adminRoles.isCategoryEdit || false,
          isCategoryDelete: user.adminRoles.isCategoryDelete || false,
          isReportView: user.adminRoles.isReportView || false,
          isReportExport: user.adminRoles.isReportExport || false,
          isPackagesView: user.adminRoles.isPackagesView || false,
          isContactsView: user.adminRoles.isContactsView || false,
        };

        setRoles(cleanedRoles);
      }
    } else {
      console.warn("Admin not found or status false:");
    }
  };

  useEffect(() => {
    getCountry();

    if (isEditMode) {
      fetchUserDetails(); // Fetch user details if in edit mode
    }
  }, [id]);

  const handlePublish = async () => {
    const validationErrors = validateUserForm(state, checkStatus, isEditMode);

    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    const compressedPic = await compressImageIfNeeded(state?.profilePic);

    setLoad(true);

    const formData = new FormData();
    formData.append("email", state?.email);
    formData.append("fullName", state?.fullName);
    formData.append("mobile", state?.mobile);
    formData.append("status", checkStatus);
    formData.append("country", state.country);
    formData.append("latitude", state.latitude);
    formData.append("longitude", state.longitude);
    formData.append("password", state.password);

    if (compressedPic instanceof File) {
      formData.append("profilePic", compressedPic);
    }

    const { data, status, message } = await apiRequest({
      endUrl: isEditMode ? _apiUrls.updateAdmin : _apiUrls.createAdmin,
      method: isEditMode ? "PUT" : "POST",
      body: formData,
      headerType: "form",
      query: isEditMode ? { adminId: id } : {},
      showMsg: true,
    });

    if (status) {
      const updatedRoles = {
        ...roles,
        adminId: data?._id,
      };

      setRoles(updatedRoles); // only update the state
      handleSetRoles(updatedRoles); // call the API separately
    }

    setLoad(false);

    navigate("/dashboard/roles");
  };

  const handleSetRoles = async (rolesData) => {
    const { data, status, message } = await apiRequest({
      endUrl: _apiUrls.setRoles,
      method: "POST",
      body: rolesData,
      showMsg: true,
    });
  };

  return (
    <>
      <Row className="align-items-start">
        <Col md={8} className="py-1">
          {/* <h4 className='dashheading'>Add User</h4>
                    <p className='dashsubheading'>Add/Edit User</p> */}
          <p className="mb-1 input-label head-border">
            Enter the details below for {isEditMode ? "edit" : "create"} Admin
          </p>
        </Col>
        <Col md={4} className="py-1">
          <div className="d-flex justify-content-end align-items-center gap-2">
            <CustomButton
              variant="contained"
              text={load ? <Spinner size="sm" animation="border" /> : "Publish"}
              iconLeft={
                !load && <CheckBoxOutlinedIcon style={{ fontSize: 18 }} />
              }
              className="containebtn"
              onClick={handlePublish}
              disabled={load}
              style={{ width: 100 }}
            />
            <CustomButton
              variant="outline"
              text={"Cancel"}
              iconLeft={
                <ArrowCircleLeftRoundedIcon
                  style={{ fontSize: 18, color: isDarkMode ? "#fff" : "#fff" }}
                />
              }
              className="outline-btn"
              onClick={() => navigate("/dashboard/usermanagement")}
            />
          </div>
        </Col>
      </Row>

      <Row>
        <Col md={8} className="py-1">
          <Row>
            {/* <Col md={12} className='pb-3'>
                            <p className='mb-1 input-label' style={{ borderBottom: '1px dashed black' }}>Enter the details below for create/edit Users</p>
                        </Col> */}

            <Col md={6}>
              <p className="mb-1 input-label">
                Name <span style={{ color: "red" }}>*</span>
              </p>
              <CustomTextFeild
                style={{
                  padding: "9px 11px",
                  borderRadius: "10px",
                }}
                className="user-input"
                placeholder="Enter User Name"
                value={state?.fullName}
                onChange={(e) =>
                  setState({ ...state, fullName: e.target.value })
                }
              />
              {errors.fullName && (
                <p className="errormsg mt-1">{errors.fullName}</p>
              )}
            </Col>
            <Col md={6}>
              <p className="mb-1 input-label">
                Email ID <span style={{ color: "red" }}>*</span>
              </p>
              <CustomTextFeild
                style={{
                  padding: "9px 11px",
                  borderRadius: "10px",
                }}
                placeholder="Enter Email Id"
                value={state?.email}
                onChange={(e) => setState({ ...state, email: e.target.value })}
              />
              {errors.email && <p className="errormsg mt-1">{errors.email}</p>}
            </Col>

            <Col md={6} className="mt-3">
              <p className="mb-1 input-label">
                Password <span style={{ color: "red" }}>*</span>
              </p>
              <CustomTextFeild
                style={{
                  padding: "9px 11px",
                  borderRadius: "10px",
                }}
                placeholder="Enter Password"
                value={state?.password}
                onChange={(e) =>
                  setState({ ...state, password: e.target.value })
                }
              />
              {errors.email && (
                <p className="errormsg mt-1">{errors.password}</p>
              )}
            </Col>

            <Col md={6} className="mt-3">
              <p className="mb-1 input-label">
                Mobile No. <span style={{ color: "red" }}>*</span>
              </p>
              <CustomTextFeild
                style={{
                  padding: "9px 11px",
                  borderRadius: "10px",
                }}
                placeholder="Enter Mobile No."
                type="number"
                value={state?.mobile}
                onChange={(e) => setState({ ...state, mobile: e.target.value })}
              />
              {errors.mobile && (
                <p className="errormsg mt-1">{errors.mobile}</p>
              )}
            </Col>

            <Col md={6} className="mt-3">
              <p className="mb-1 input-label">
                Country <span style={{ color: "red" }}>*</span>
              </p>
              <CustomSelect
                className="custom-select"
                placeholder="Select Country"
                style={{ padding: "9px 11px", marginBottom: 0 }}
                options={country.rows}
                onChange={handleSelectChange}
                value={
                  country.rows.find((option) => option.value === state.country)
                    ?.value || ""
                }
              />
              {errors.country && <p className="errormsg">{errors.country}</p>}
            </Col>

            <Col md={6} className="mt-3">
              <p className="mb-1 input-label">Profile Image </p>
              <Form.Group controlId="formFile">
                <Form.Control
                  type="file"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setState({
                        ...state,
                        profilePic: file, // keep the File for upload
                        previewPic: URL.createObjectURL(file), // create preview URL
                      });
                    }
                  }}
                  className="fileInput"
                  style={{
                    padding: "8px 12px",
                    border: "1px solid #C8C8C8",
                    borderRadius: "10px",
                  }}
                />

                {/* ----------------------------  */}
                {isEditMode && (state.previewPic || state.profilePic) && (
                  <div className="mt-3">
                    <img
                      src={
                        state.previewPic
                          ? state.previewPic
                          : typeof state.profilePic === "string"
                            ? state.profilePic.startsWith("http")
                              ? state.profilePic
                              : `${process.env.REACT_APP_API_ORIGIN}/${state.profilePic.replace(/^\/+/, "")}`
                            : ""
                      }
                      alt="profile"
                      style={{
                        width: 100,
                        height: 100,
                        borderRadius: "10px",
                        objectFit: "cover",
                        border: "1px solid #ddd",
                      }}
                    />
                  </div>
                )}
              </Form.Group>

              {errors.profilePic && (
                <p className="errormsg mt-1">{errors.profilePic}</p>
              )}
            </Col>

            <Col md={6} className="mt-3">
              <p className="mb-1 input-label">Latitude</p>
              <CustomTextFeild
                style={{
                  padding: "9px 11px",
                  borderRadius: "10px",
                }}
                placeholder="Latitude"
                type="number"
                value={state?.latitude}
                onChange={(e) =>
                  setState({ ...state, latitude: e.target.value })
                }
              />
            </Col>

            <Col md={6} className="mt-3">
              <p className="mb-1 input-label">Longitude</p>
              <CustomTextFeild
                style={{
                  padding: "9px 11px",
                  borderRadius: "10px",
                }}
                placeholder="Longitude"
                type="number"
                value={state?.longitude}
                onChange={(e) =>
                  setState({ ...state, longitude: e.target.value })
                }
              />
            </Col>
            <Col md={12} className="mt-3">
              <CustomButton
                variant="outline"
                text={"Fetch Location"}
                className="outline-btn"
                onClick={() => setShowMapPicker(true)}
              />
            </Col>
          </Row>
        </Col>

        <Col md={4}>
          <div className="status-card">
            <h5 className="status-title">Status Update</h5>
            <div className="status-option mb-3 mt-4">
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <input
                  type="checkbox"
                  checked={checkStatus === "active"}
                  onChange={() => handleChange("active")}
                />
                <p className="mb-0">Active</p>
              </div>
            </div>
            <div className="status-option">
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <input
                  type="checkbox"
                  checked={checkStatus === "inactive"}
                  onChange={() => handleChange("inactive")}
                />
                <p className="mb-0">InActive</p>
              </div>
            </div>
          </div>
          {errors.status && (
            <small style={{ color: "red", display: "block", marginTop: "8px" }}>
              {errors.status}
            </small>
          )}
        </Col>
      </Row>

      <Col md={12}>
        <div className="status-card">
          <h5 className="status-title">Roles & Rights</h5>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "16px" }}>
            {Object.keys(roles).map((roleKey) => {
              if (roleKey === "adminId") return null;

              const labelMap = {
                isUserEdit: "User Edit",
                isNotificationEdit: "Notification Edit",
                isUserDelete: "User Delete",
                isNotificationDelete: "Notification Delete",
                isCategoryEdit: "Category Edit",
                isCategoryDelete: "Category Delete",
                isReportView: "Report View",
                isReportExport: "Report Export",
                isPackagesView: "Packages View",
                isContactsView: "Contacts View",
              };

              return (
                <div
                  key={roleKey}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    minWidth: "200px", // ensures at least two side-by-side
                  }}
                  className="status-option mb-3 mt-2"
                >
                  <input
                    type="checkbox"
                    checked={roles[roleKey]}
                    onChange={() =>
                      setRoles((prev) => ({
                        ...prev,
                        [roleKey]: !prev[roleKey],
                      }))
                    }
                  />
                  <p className="mb-0"> {labelMap[roleKey] || roleKey}</p>
                </div>
              );
            })}
          </div>
        </div>

        {errors.status && (
          <small style={{ color: "red", display: "block", marginTop: "8px" }}>
            {errors.status}
          </small>
        )}
      </Col>

      <MapLocationPicker
        isOpen={showMapPicker}
        onClose={() => setShowMapPicker(false)}
        onLocationSelect={handleLocationSelect}
        initialLat={state.latitude}
        initialLng={state.longitude}
        title="Select User Location"
      />
    </>
  );
}
